import React, { useState, useEffect } from "react";
import { FactureList } from "../../components/factures/FactureList";
import { FactureService } from "../../services/factures/factureService";
import { PaiementService } from "../../services/paiements/paiementService";
import { toast } from "react-hot-toast";
import { Check, X, CreditCard, ShieldCheck, Eye } from "lucide-react";
import { FactureViewerModal } from "../../components/factures/FactureViewerModal";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import { useTranslation } from "react-i18next";
import { downloadFactureFile } from "../../utils/factureDownload";

interface Facture {
  id: string;
  numero: string;
  fournisseur_nom: string;
  client_nom?: string;
  montant_ttc: number;
  montant_avance?: number;
  montant_net?: number;
  frais_escompte?: number;
  commission_succes?: number;
  date_emission: string;
  date_echeance: string;
  statut: string;
  score_decision?: number;
  decision_paiement?: string;
  valide_par?: string;
  fichier_original?: string;
}

export const AdminFactures: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { user } = useSelector((state: RootState) => state.auth);
  const isFinance = user?.role === 'finance' || user?.role === 'admin' || user?.role === 'super_admin';
  const [factures, setFactures] = useState<Facture[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedFacture, setSelectedFacture] = useState<Facture | null>(null);
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [comment, setComment] = useState("");
  const [processingAction, setProcessingAction] = useState(false);
  const [comptesBancaires, setComptesBancaires] = useState<any[]>([]);
  const [selectedCompteId, setSelectedCompteId] = useState("");
  const [showViewer, setShowViewer] = useState(false);

  const fetchFactures = async () => {
    try {
      setLoading(true);
      const response = await FactureService.getFactures();
      setFactures(response.data.results);
    } catch (error) {
      console.error("Erreur chargement factures", error);
      toast.error(t('factures.management.errorValidation')); // Reuse or find better
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFactures();
    fetchFactures();
    if (isFinance) {
      PaiementService.getComptesBancairesActifs()
        .then(res => setComptesBancaires(res.data))
        .catch(err => console.error("Erreur chargement comptes bancaires", err));
    }
  }, [isFinance]);

  const handleFactureClick = (facture: Facture) => {
    // Open detail or validation modal
    setSelectedFacture(facture);
    setShowValidationModal(true);
    setComment("");
    setSelectedCompteId("");
  };

  const handleValidate = async () => {
    if (!selectedFacture) return;
    
    try {
      setProcessingAction(true);
      await FactureService.validerFacture(selectedFacture.id);
      toast.success(`${t('factures.title')} ${selectedFacture.numero} ${t('factures.management.successValidated')}`);
      setShowValidationModal(false);
      fetchFactures();
    } catch (error) {
      console.error("Erreur validation", error);
      toast.error(t('factures.management.errorValidation'));
    } finally {
      setProcessingAction(false);
    }
  };

  const handleReject = async () => {
    if (!selectedFacture) return;
    
    if (!comment) {
        toast.error(t('factures.management.errorCommentRequired'));
        return;
    }

    try {
      setProcessingAction(true);
      await FactureService.rejeterFacture(selectedFacture.id, comment);
      toast.success(`${t('factures.title')} ${selectedFacture.numero} ${t('factures.management.successRejected')}`);
      setShowValidationModal(false);
      fetchFactures();
    } catch (error) {
      console.error("Erreur rejet", error);
      toast.error(t('factures.management.errorReject'));
    } finally {
      setProcessingAction(false);
    }
  };

  const handleApproveScoring = async () => {
    if (!selectedFacture) return;
    if (!selectedCompteId) {
      toast.error(t('factures.management.errorAccountRequired'));
      return;
    }
    try {
      setProcessingAction(true);
      await FactureService.approuverScoring(selectedFacture.id, comment || undefined, selectedCompteId);
      toast.success(`${t('factures.management.successScoringApproved')} ${selectedFacture.numero}`);
      setShowValidationModal(false);
      fetchFactures();
    } catch (error) {
      console.error("Erreur approbation scoring", error);
      toast.error(t('common.errorGroup'));
    } finally {
      setProcessingAction(false);
    }
  };

  const handleRejectScoring = async () => {
    if (!selectedFacture) return;
    if (!comment) {
      toast.error(t('factures.management.errorCommentRequired'));
      return;
    }
    try {
      setProcessingAction(true);
      await FactureService.rejeterScoring(selectedFacture.id, comment);
      toast.success(`${t('factures.management.successScoringRejected')} ${selectedFacture.numero}`);
      setShowValidationModal(false);
      fetchFactures();
    } catch (error) {
      console.error("Erreur rejet scoring", error);
      toast.error(t('common.errorGroup'));
    } finally {
      setProcessingAction(false);
    }
  };

  const handleDownload = async (facture: Facture) => {
    try {
      const ok = await downloadFactureFile(facture.id, facture.numero);
      if (!ok) toast.error(t('factures.management.noFile'));
    } catch (error) {
      console.error("Erreur téléchargement", error);
      toast.error(t('factures.management.downloadError'));
    }
  };

  if (loading && factures.length === 0) {
      return (
          <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
      );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('factures.management.title')}</h1>
          <p className="mt-1 text-sm text-gray-600">
            {t('factures.management.overview')}
          </p>
        </div>
      </div>

      <FactureList 
        factures={factures} 
        onFactureClick={handleFactureClick}
        onDownload={handleDownload}
        renderActions={(facture) => (
            <div className="flex items-center space-x-2">
                {facture.statut === 'soumise' && (
                    <>
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                setSelectedFacture(facture);
                                setShowValidationModal(true);
                                setComment("");
                                setSelectedCompteId("");
                            }}
                            className="p-1 px-3 bg-green-100 text-green-700 rounded hover:bg-green-200 text-xs font-medium flex items-center"
                        >
                            <Check className="h-3 w-3 mr-1" />
                            {t('factures.management.validate')}
                        </button>
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                setSelectedFacture(facture);
                                setShowValidationModal(true);
                                setComment("");
                                setSelectedCompteId("");
                            }}
                            className="p-1 px-3 bg-red-100 text-red-700 rounded hover:bg-red-200 text-xs font-medium flex items-center"
                        >
                            <X className="h-3 w-3 mr-1" />
                            {t('factures.management.reject')}
                        </button>
                    </>
                )}
                {facture.statut === 'en_scoring' && isFinance && (
                    <>
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                setSelectedFacture(facture);
                                setShowValidationModal(true);
                                setComment("");
                                setSelectedCompteId("");
                            }}
                            className="p-1 px-3 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 text-xs font-medium flex items-center"
                        >
                            <CreditCard className="h-3 w-3 mr-1" />
                            {t('factures.management.approveAndPay')}
                        </button>
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                setSelectedFacture(facture);
                                setShowValidationModal(true);
                                setComment("");
                                setSelectedCompteId("");
                            }}
                            className="p-1 px-3 bg-red-100 text-red-700 rounded hover:bg-red-200 text-xs font-medium flex items-center"
                        >
                            <X className="h-3 w-3 mr-1" />
                            {t('factures.management.reject')}
                        </button>
                    </>
                )}
            </div>
        )}
      />

      {/* Modal de validation - Reused logic */}
      {showValidationModal && selectedFacture && (
        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 transition-opacity"
              aria-hidden="true"
              onClick={() => setShowValidationModal(false)}
            >
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <div className="flex items-center justify-between">
                      <h3
                        className="text-lg leading-6 font-medium text-gray-900"
                        id="modal-title"
                      >
                        {t('factures.management.invoiceAction')}: {selectedFacture.numero}
                      </h3>
                      <button
                        onClick={() => setShowViewer(true)}
                        className="inline-flex items-center px-3 py-1 border border-primary-300 text-sm font-medium rounded-md text-primary-700 bg-primary-50 hover:bg-primary-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        {t('factures.management.viewPDF')}
                      </button>
                    </div>
                    
                    <div className="mt-4 space-y-4">
                        <div className="grid grid-cols-2 gap-4 bg-gray-50 p-3 rounded">
                             <div>
                                <p className="text-xs text-gray-500">{t('factures.supplier')}</p>
                                <p className="font-medium">{selectedFacture.fournisseur_nom}</p>
                            </div>
                            <div>
                                <p className="text-xs text-gray-500">{t('factures.amount')}</p>
                                <p className="font-medium">{new Intl.NumberFormat(i18n.language.startsWith('fr') ? 'fr-FR' : 'en-US', { style: "currency", currency: "XOF" }).format(selectedFacture.montant_ttc)}</p>
                            </div>
                        </div>

                      {/* Scoring details for en_scoring status */}
                      {selectedFacture.statut === 'en_scoring' && (
                        <div className="space-y-3">
                          <div className="flex items-center space-x-2 mb-2">
                            <ShieldCheck className="h-5 w-5 text-blue-600" />
                            <span className="text-sm font-semibold text-blue-700">{t('factures.management.scoringValidated')}</span>
                          </div>
                          <div className="grid grid-cols-2 gap-3 bg-blue-50 p-3 rounded border border-blue-100">
                            <div>
                              <p className="text-xs text-gray-500">{t('factures.management.scoringValidated')}</p>
                              <p className="text-lg font-bold text-gray-900">{selectedFacture.score_decision ?? '—'}/100</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">{t('factures.management.autoDecision')}</p>
                              <p className="text-sm font-medium text-orange-600">{t('factures.management.manualReview')}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">{t('commissions.fees.discount')}</p>
                              <p className="font-medium">{new Intl.NumberFormat(i18n.language.startsWith('fr') ? 'fr-FR' : 'en-US', { style: "currency", currency: "XOF" }).format(selectedFacture.frais_escompte || 0)}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">{t('commissions.fees.success')}</p>
                              <p className="font-medium">{new Intl.NumberFormat(i18n.language.startsWith('fr') ? 'fr-FR' : 'en-US', { style: "currency", currency: "XOF" }).format(selectedFacture.commission_succes || 0)}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">{t('paiements.amount_advanced')}</p>
                              <p className="font-medium">{new Intl.NumberFormat(i18n.language.startsWith('fr') ? 'fr-FR' : 'en-US', { style: "currency", currency: "XOF" }).format(selectedFacture.montant_avance || 0)}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">{t('factures.management.netAmount')}</p>
                              <p className="font-bold text-green-700">{new Intl.NumberFormat(i18n.language.startsWith('fr') ? 'fr-FR' : 'en-US', { style: "currency", currency: "XOF" }).format(selectedFacture.montant_net || 0)}</p>
                            </div>
                          </div>
                        </div>
                      )}

                      {(selectedFacture.statut === 'soumise' || selectedFacture.statut === 'en_scoring') ? (
                        <div className="space-y-4">
                          {selectedFacture.statut === 'en_scoring' && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('factures.management.issuerAccount')}
                                </label>
                                <select
                                    required
                                    value={selectedCompteId}
                                    onChange={(e) => setSelectedCompteId(e.target.value)}
                                    className="block w-full rounded-md border border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm py-2 px-3"
                                >
                                    <option value="">{t('factures.management.selectAccount')}</option>
                                    {comptesBancaires.map((compte) => (
                                        <option key={compte.id} value={compte.id}>
                                            {compte.nom} ({compte.banque}) - {compte.iban}
                                        </option>
                                    ))}
                                </select>
                            </div>
                          )}
                          <div>
                             <label
                                htmlFor="comment"
                                className="block text-sm font-medium text-gray-700"
                            >
                                {t('factures.management.commentLabel')}
                            </label>
                            <textarea
                                id="comment"
                                rows={3}
                                className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                                placeholder={t('factures.management.commentPlaceholder')}
                                value={comment}
                                onChange={(e) => setComment(e.target.value)}
                            />
                          </div>
                        </div>
                      ) : (
                        <div>
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                            {t('factures.status')}: {selectedFacture.statut}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                {selectedFacture.statut === 'soumise' ? (
                  <>
                    <button
                      type="button"
                      onClick={handleValidate}
                      disabled={processingAction}
                      className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-success-600 text-base font-medium text-white hover:bg-success-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-success-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                    >
                      {processingAction ? "..." : t('factures.management.validate')}
                    </button>
                    <button
                      type="button"
                      onClick={handleReject}
                      disabled={processingAction}
                      className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-red-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                    >
                      {processingAction ? "..." : t('factures.management.reject')}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowValidationModal(false)}
                      disabled={processingAction}
                      className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                    >
                      {t('common.cancel')}
                    </button>
                  </>
                ) : selectedFacture.statut === 'en_scoring' && isFinance ? (
                  <>
                    <button
                      type="button"
                      onClick={handleApproveScoring}
                      disabled={processingAction}
                      className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                    >
                      <CreditCard className="h-4 w-4 mr-2" />
                      {processingAction ? "..." : t('factures.management.approveScoring')}
                    </button>
                    <button
                      type="button"
                      onClick={handleRejectScoring}
                      disabled={processingAction}
                      className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-red-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                    >
                      {processingAction ? "..." : t('factures.management.rejectScoring')}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowValidationModal(false)}
                      disabled={processingAction}
                      className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                    >
                      {t('common.cancel')}
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={() => setShowValidationModal(false)}
                    disabled={processingAction}
                    className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:w-auto sm:text-sm"
                  >
                    {t('common.close')}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {selectedFacture && (
        <FactureViewerModal
          isOpen={showViewer}
          onClose={() => setShowViewer(false)}
          factureUrl={selectedFacture.fichier_original || ""}
          factureNumero={selectedFacture.numero}
        />
      )}
    </div>
  );
};
